<template lang="pug">
.file-field
  .form-field-label(v-if="label")
    span {{ label }}

  .form-field.form-file
    .file-drag-drop-area(v-if="inputValues.length === 0", :id="`file-field-${name}`", @click="openFileInput")
      img.file-field-icon(:src="require('../../../../assets/icons/form/fields/file_field_icon.svg')")
      span.drop-files {{ fileFieldLocales.sublabel }}
      q-file(
        v-show="false",
        filled,
        v-model="inputValues",
        :multiple="multiple",
        :label="label",
        :for="`file-input-${name}`"
      )
    .form-preview-area(v-else)
      .preview-file(v-for="(file, index) in inputValues")
        img.preview-img(v-if="isImage(file.type)", :ref="el => (previewImageRefs[index] = el)")
        inline-svg.preview-doc(v-else, :src="require('../../../../assets/icons/document.svg')")
        span.caption {{ cutFileName(file.name) }}

    q-icon.cancel-file-field(v-if="inputValues.length > 0", name="cancel", @click.stop="resetValue()")

  .form-image-area.justify-center(v-if="loadedFiles.length > 0")
    .files-area-wrapper
      .files-area-body
        .existing-file(v-for="file in loadedFiles")
          .form-image(v-if="isImage(file.content_type)")
            a.file-link(v-if="file", :href="file.url", target="_blank", :link_title="file.file_name")
              img.file-image(:src="file.url")
              span.caption {{ cutFileName(file.file_name) }}

          .form-image(v-else)
            a.file-link(v-if="file", :href="file.url", target="_blank", :link_title="file.file_name")
              inline-svg.file-document(:src="require('../../../../assets/icons/document.svg')")
              span.caption {{ cutFileName(file.file_name) }}
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from "vue";
import { fileFieldLocales } from "@/services/useLocales";

const props = defineProps({
  /* Лейбл чекбокса, строка */
  label: { type: String || null, default: null, required: false },
  /* Начальное значение поля, массив файлов или пустой массив */
  loadedFiles: { type: Array, default: () => [], required: false },
  /* Уникальное имя для генерации id файл инпута. Без него не будет ничего работать. Принимает строку */
  name: { type: String, default: "file-field", required: true },
  /* Возможность загрузки сразу нескольких файлов. Принемает true/false */
  multiple: { type: Boolean, default: false, required: false },
});

const emit = defineEmits(["change", "reset"]);

const inputValues = ref([]);
const loadedFiles = ref(props.loadedFiles);
const previewImageRefs = ref([]);

const isImage = type => {
  return ["image/jpg", "image/gif", "image/jpeg", "image/png"].includes(type);
};

const openFileInput = () => {
  document.getElementById(`file-input-${props.name}`).click();
};

const cutFileName = name => {
  const fileType = name.match(/\.(\w+)$/)[0];
  return name?.length > 15 ? `${name.substring(0, 13)}..${fileType}` : name;
};

const getFiles = async value => {
  if (Array.isArray(value)) {
    const files = [];

    value.map(file => {
      files.push({
        name: file.name,
        file: file,
        type: file.type,
      });
    });

    changeValue(files);

    await nextTick();
    getImagePreviews();
  }
};

const getImagePreviews = () => {
  if (inputValues.value?.length > 0) {
    inputValues.value.map((file, i) => {
      if (["image/jpg", "image/gif", "image/jpeg", "image/png"].includes(file.type)) {
        const reader = new FileReader();
        reader.addEventListener(
          "load",
          function () {
            previewImageRefs.value[i].src = reader.result;
          },
          false,
        );
        reader.readAsDataURL(file);
      }
    });
  }
};

const changeValue = val => {
  emit("change", val);
};

const resetValue = () => {
  inputValues.value = [];
  changeValue([]);
};

const determineDragAndDropCapable = () => {
  const div = document.createElement("div");
  return (
    ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) && "FormData" in window && "FileReader" in window
  );
};

const setDragAndDrop = () => {
  const input = document.getElementById(`file-field-${props.name}`);
  const dragAndDropCapable = determineDragAndDropCapable();
  if (dragAndDropCapable) {
    ["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop"].forEach(function (evt) {
      input.addEventListener(
        evt,
        function (e) {
          e.preventDefault();
          e.stopPropagation();
        },
        false,
      );
    });
    input.addEventListener("drop", function (e) {
      let files = props.multiple ? e.dataTransfer.files : [e.dataTransfer.files[0]];
      files = Object.values(files);
      inputValues.value = files;
      getFiles(files);
    });
  }
};

watch(
  () => inputValues.value,
  (newValue, oldValue) => {
    if (!Array.isArray(newValue)) inputValues.value = [newValue];
    getFiles(newValue);
  },
  { deep: true },
);

onMounted(() => {
  setDragAndDrop();
});
</script>
